import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Column } from '@miq/catalog';
import { PropertySetColumnStateService } from './property-set-column.state';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';
import { digger } from '@cbre/digger';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DASH } from '@miq/utils';

export interface PropertySetColumnComponentConfig {
  getValueByUsage: boolean;
  showTooltip: boolean;
  multiLineTootltip: boolean;
  maxCharacters: number;
}

interface PropertySetColumn extends Column {
  replaceEmptyWith: string;
  componentConfig: PropertySetColumnComponentConfig;
}

@Component({
  selector: 'miq-property-sets-column',
  template: `
    <ng-container *ngIf="propertySetList$ | async as propertySetList">
      <div
        class="ps-column-container"
        [matTooltip]="propertySetList.fullValue"
        matTooltipPosition="above"
        [matTooltipDisabled]="!column?.componentConfig?.showTooltip"
        [matTooltipClass]="column?.componentConfig?.multiLineTootltip ? 'multiline-tooltip' : ''"
      >
        {{ propertySetList?.truncatedValue }}
      </div>
    </ng-container>
  `,
  imports: [CommonModule, MatButtonModule, MatTooltipModule],
  // mirrors the line-clamp rule record-list applies to span cells, preventing div from expanding fixed-height table rows
  styles: [
    `
      .ps-column-container {
        display: -webkit-box;
        max-height: 3.6em;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: break-word;
        word-break: break-word;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertySetColumnComponent implements OnChanges, OnInit {
  @Input() record: Record<string, any>;
  @Input() column: PropertySetColumn;

  #propertySetList = new BehaviorSubject<boolean>(false);
  propertySetList$: Observable<{ truncatedValue: string; fullValue: string }>;

  constructor(private psColumnState: PropertySetColumnStateService) {}

  ngOnInit() {
    this.propertySetList$ = combineLatest([
      this.psColumnState.propertySetsByUser$,
      this.#propertySetList.asObservable().pipe(filter(Boolean)),
    ]).pipe(map(this.#filterAndGetValues));

    if (this.record != null && this.column != null) {
      this.#propertySetList.next(true);
    }
  }

  #getRecordPSValues = (record: Record<string, any>, column: PropertySetColumn) => {
    const value = digger(record, column.field || column.path);
    if (value == null || value == '') return [];
    return Array.isArray(value) ? value : [value];
  };

  #getValuesByUsage = (
    record: Record<string, any>,
    column: PropertySetColumn,
    psMapping: Record<string, string>
  ): string[] => {
    const extractedValuesByUsage = [];
    record.usages.forEach(usage => {
      const replaceWith = column?.replaceEmptyWith || DASH;
      let usageValue = digger(usage, 'record_set/set_id') || replaceWith;
      usageValue = Array.isArray(usageValue) ? usageValue : [usageValue];
      usageValue = usageValue.map(uv => psMapping[uv]).filter(Boolean) || [];
      extractedValuesByUsage.push(usageValue.join(', ') || replaceWith);
    });
    return extractedValuesByUsage;
  };

  #getDefault = (record: Record<string, any>, column: PropertySetColumn, psMapping: Record<string, string>): string[] =>
    this.#getRecordPSValues(record, column)
      .map(el => psMapping[el])
      .filter(Boolean);

  #filterAndGetValues = (updatedData: [Record<string, string>, boolean]) => {
    const [psMapping] = updatedData;
    const extractValueFn = this.column.componentConfig?.getValueByUsage ? this.#getValuesByUsage : this.#getDefault;
    const extractedValue = extractValueFn(this.record, this.column, psMapping);
    const joinBy = this.column.componentConfig?.multiLineTootltip ? '\n' : ', ';
    const fullValue: string = extractedValue.join(joinBy) || '';
    let truncatedValue = extractedValue.join(', ') || (this.column?.replaceEmptyWith ?? '');

    if (
      this.column.componentConfig?.maxCharacters &&
      truncatedValue.length > this.column.componentConfig.maxCharacters
    ) {
      truncatedValue = `${truncatedValue.slice(0, this.column.componentConfig.maxCharacters - 3)}...`;
    }
    return { truncatedValue, fullValue };
  };

  ngOnChanges(changes: SimpleChanges) {
    const { record, column } = changes;
    if (record?.currentValue != null && column?.currentValue != null) {
      this.#propertySetList.next(true);
    }
  }
}
